import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, Trophy, Calendar, Award, FileText, Clock } from 'lucide-react';
import { hackathonAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FormWizard from '../../components/ui/FormWizard';
import DateTimePicker from '../../components/ui/DateTimePicker';
import { useToast } from '../../contexts/ToastContext';

interface ProblemStatement {
  title: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  isHiddenFromTeams?: boolean;
}

interface Prize {
  rank: number;
  description: string;
  value: string;
}

interface Phase {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  order: number;
  deliverables: Array<{
    name: string;
    description: string;
    required: boolean;
  }>;
}

const HackathonCreate: React.FC = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [showValidation, setShowValidation] = useState(false);
  const [stepValidation, setStepValidation] = useState<boolean[]>([true, true, true, true, true, true, true]);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [flyerPreview, setFlyerPreview] = useState<string>('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxTeamSize: 7,
    minTeamSize: 1,
    maxTeams: 50,
    requiresTeamApproval: true,
    status: 'upcoming',
    isActive: true,
    hackathonFlyer: '',
    showInHomeCarousel: false,
    problemStatements: [] as ProblemStatement[],
    prizes: [] as Prize[],
    phases: [] as Phase[],
    rules: [] as string[],
    requirements: [] as string[]
  });

  // Debug effect to track hackathonFlyer changes
  useEffect(() => {
    console.log('🔍 hackathonFlyer state changed:', formData.hackathonFlyer);
    console.log('🔍 Checkbox should be enabled:', !!formData.hackathonFlyer);
  }, [formData.hackathonFlyer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseInt(value) : value
    }));
    
    // Immediate validation for required basic fields
    if (name === 'title' || name === 'description') {
      setTimeout(() => {
        const currentErrors = { ...validationErrors };
        
        if (name === 'title') {
          if (!value.trim()) {
            currentErrors.title = 'Title is required';
          } else {
            delete currentErrors.title;
          }
        }
        
        if (name === 'description') {
          if (!value.trim()) {
            currentErrors.description = 'Description is required';
          } else {
            delete currentErrors.description;
          }
        }
        
        setValidationErrors(currentErrors);
        updateStepValidation();
      }, 100);
    } else {
      // Update step validation after input change
      setTimeout(() => updateStepValidation(), 100);
    }
  };

  // Handle hackathon flyer upload
  const handleFlyerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('📁 File selected:', file.name, file.type, file.size);

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      error('Only JPEG, PNG, GIF and WebP images are allowed');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      error('File size must be less than 10MB');
      return;
    }

    setUploadingFlyer(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('flyer', file);

      console.log('🌐 Uploading to:', `/api/upload/hackathon-flyer`);
      
      const response = await fetch(`/api/upload/hackathon-flyer`, {
        method: 'POST',
        body: uploadFormData,
        credentials: 'include'
      });

      console.log('📡 Upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload failed:', response.status, errorText);
        throw new Error('Upload failed');
      }

      const result = await response.json();
      console.log('✅ Upload result:', result);
      
      if (result.success) {
        console.log('🔗 Received flyer URL from backend:', result.data.flyerUrl);
        console.log('🔍 Flyer URL type:', typeof result.data.flyerUrl);
        setFormData(prev => ({ ...prev, hackathonFlyer: result.data.flyerUrl }));
        setFlyerPreview(result.data.flyerUrl);
        console.log('🖼️ Flyer preview set to:', result.data.flyerUrl);
        success('Hackathon flyer uploaded successfully');
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('💥 Flyer upload error:', err);
      error('Failed to upload hackathon flyer');
    } finally {
      setUploadingFlyer(false);
    }
  };

  // Handle flyer removal
  const handleRemoveFlyer = () => {
    setFormData(prev => ({ ...prev, hackathonFlyer: '' }));
    setFlyerPreview('');
    success('Hackathon flyer removed');
  };

  // Problem Statement functions
  const addProblemStatement = () => {
    setFormData(prev => ({
      ...prev,
      problemStatements: [...prev.problemStatements, {
        title: '',
        description: '',
        category: '',
        difficulty: 'medium',
        isHiddenFromTeams: false
      }]
    }));
  };

  const updateProblemStatement = (index: number, field: keyof ProblemStatement, value: any) => {
    setFormData(prev => ({
      ...prev,
      problemStatements: prev.problemStatements.map((problem, i) => 
        i === index ? { ...problem, [field]: value } : problem
      )
    }));
    setTimeout(() => updateStepValidation(), 100);
  };

  const removeProblemStatement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      problemStatements: prev.problemStatements.filter((_, i) => i !== index)
    }));
  };

  // Prize functions
  const addPrize = () => {
    setFormData(prev => ({
      ...prev,
      prizes: [...prev.prizes, { rank: prev.prizes.length + 1, description: '', value: '' }]
    }));
  };

  const updatePrize = (index: number, field: keyof Prize, value: any) => {
    setFormData(prev => ({
      ...prev,
      prizes: prev.prizes.map((prize, i) => 
        i === index ? { ...prize, [field]: value } : prize
      )
    }));
    setTimeout(() => updateStepValidation(), 100);
  };

  const removePrize = (index: number) => {
    setFormData(prev => ({
      ...prev,
      prizes: prev.prizes.filter((_, i) => i !== index).map((prize, i) => ({
        ...prize,
        rank: i + 1
      }))
    }));
  };

  // Phase functions
  const addPhase = () => {
    setFormData(prev => ({
      ...prev,
      phases: [...prev.phases, {
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        order: prev.phases.length + 1,
        deliverables: []
      }]
    }));
  };

  const updatePhase = (index: number, field: keyof Phase, value: any) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => 
        i === index ? { ...phase, [field]: value } : phase
      )
    }));
    setTimeout(() => updateStepValidation(), 100);
  };

  const removePhase = (index: number) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index).map((phase, i) => ({
        ...phase,
        order: i + 1
      }))
    }));
  };

  const addDeliverable = (phaseIndex: number) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => 
        i === phaseIndex 
          ? { ...phase, deliverables: [...phase.deliverables, { name: '', description: '', required: false }] }
          : phase
      )
    }));
  };

  const updateDeliverable = (phaseIndex: number, deliverableIndex: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => 
        i === phaseIndex 
          ? { 
              ...phase, 
              deliverables: phase.deliverables.map((deliverable, j) => 
                j === deliverableIndex ? { ...deliverable, [field]: value } : deliverable
              )
            }
          : phase
      )
    }));
  };

  const removeDeliverable = (phaseIndex: number, deliverableIndex: number) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => 
        i === phaseIndex 
          ? { 
              ...phase, 
              deliverables: phase.deliverables.filter((_, j) => j !== deliverableIndex)
            }
          : phase
      )
    }));
  };

  // Rules and Requirements functions
  const addRule = () => {
    setFormData(prev => ({
      ...prev,
      rules: [...prev.rules, '']
    }));
  };

  const updateRule = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => i === index ? value : rule)
    }));
    setTimeout(() => updateStepValidation(), 100);
  };

  const removeRule = (index: number) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const updateRequirement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }));
  };

  const removeRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  // Validation functions - matching schema constraints
  const isBasicInfoValid = () => {
    const title = formData.title.trim();
    const description = formData.description.trim();
    const startDate = formData.startDate?.trim() || '';
    const endDate = formData.endDate?.trim() || '';
    const registrationDeadline = formData.registrationDeadline?.trim() || '';
    
    // Basic field validation
    const basicFieldsValid = title.length >= 1 && title.length <= 100 && // Schema: minlength: 1, maxlength: 100
           description.length >= 1 && description.length <= 2000 && // Schema: minlength: 1, maxlength: 2000
           startDate !== '' && 
           endDate !== '' && 
           registrationDeadline !== '' &&
           formData.maxTeams >= 1 && formData.maxTeams <= 1000 && // Schema: min: 1, max: 1000
           formData.minTeamSize >= 1 && formData.minTeamSize <= 5 && // Schema: min: 1, max: 5
           formData.maxTeamSize >= 1 && formData.maxTeamSize <= 10 && // Schema: min: 1, max: 10
           formData.minTeamSize <= formData.maxTeamSize; // Schema validation

    // Date logic validation
    let dateLogicValid = true;
    if (startDate && endDate) {
      dateLogicValid = dateLogicValid && new Date(startDate) < new Date(endDate);
    }
    if (registrationDeadline && startDate) {
      dateLogicValid = dateLogicValid && new Date(registrationDeadline) < new Date(startDate);
    }

    const isValid = basicFieldsValid && dateLogicValid;

    // Debug logging for troubleshooting
    console.log('Basic Info Validation Check:', {
      title: { value: title, valid: title.length >= 1 && title.length <= 100 },
      description: { value: description, valid: description.length >= 1 && description.length <= 2000 },
      startDate: { value: startDate, valid: startDate !== '' },
      endDate: { value: endDate, valid: endDate !== '' },
      registrationDeadline: { value: registrationDeadline, valid: registrationDeadline !== '' },
      basicFieldsValid,
      dateLogicValid,
      dateLogic: {
        endAfterStart: startDate && endDate ? new Date(startDate) < new Date(endDate) : 'n/a',
        regBeforeStart: registrationDeadline && startDate ? new Date(registrationDeadline) < new Date(startDate) : 'n/a'
      },
      isValid
    });
    
    return isValid;
  };

  const isPhasesValid = () => {
    // If no phases exist, this step is valid
    if (formData.phases.length === 0) {
      return true;
    }
    
    // If phases exist, they must all be complete
    return formData.phases.every(phase => 
      phase.name.trim() !== '' && phase.name.length <= 100 && // Schema: maxlength: 100
      phase.description.trim() !== '' && phase.description.length <= 500 && // Schema: maxlength: 500
      phase.startDate !== '' && 
      phase.endDate !== ''
    );
  };

  const isProblemsValid = () => {
    return formData.problemStatements.length === 0 || 
           formData.problemStatements.every(problem => 
             problem.title.trim() !== '' && problem.title.length <= 200 && // Schema: maxlength: 200
             problem.description.trim() !== '' && problem.description.length <= 1000 && // Schema: maxlength: 1000
             problem.category.trim() !== '' && problem.category.length <= 100 // Schema: maxlength: 100
           );
  };

  const isPrizesValid = () => {
    // If no prizes exist, this step is valid
    if (formData.prizes.length === 0) {
      return true;
    }
    
    // If prizes exist, they must all be complete
    return formData.prizes.every(prize => 
      prize.description.trim() !== '' && prize.description.length <= 200 && // Schema: maxlength: 200
      prize.value.trim() !== '' && prize.value.length <= 100 && // Schema: maxlength: 100
      prize.rank >= 1 // Schema: min: 1
    );
  };

  const isRulesValid = () => {
    // If no rules exist, this step is valid
    if (formData.rules.length === 0) {
      return true;
    }
    
    // If rules exist, they must all be complete
    return formData.rules.every(rule => rule.trim() !== '' && rule.length <= 500); // Schema: maxlength: 500
  };

  const validateAllFields = () => {
    const errors: {[key: string]: string} = {};

    // Basic Info Validation
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      errors.title = 'Title must be 100 characters or less';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length > 2000) {
      errors.description = 'Description must be 2000 characters or less';
    }

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    }

    if (!formData.registrationDeadline) {
      errors.registrationDeadline = 'Registration deadline is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      errors.endDate = 'End date and time must be after start date and time';
    }

    if (formData.registrationDeadline && formData.startDate && new Date(formData.registrationDeadline) >= new Date(formData.startDate)) {
      errors.registrationDeadline = 'Registration deadline must be before start date and time';
    }

    if (formData.maxTeams < 1 || formData.maxTeams > 1000) {
      errors.maxTeams = 'Max teams must be between 1 and 1000';
    }

    if (formData.minTeamSize < 1 || formData.minTeamSize > 5) {
      errors.minTeamSize = 'Min team size must be between 1 and 5';
    }

    if (formData.maxTeamSize < 1 || formData.maxTeamSize > 10) {
      errors.maxTeamSize = 'Max team size must be between 1 and 10';
    }

    if (formData.minTeamSize > formData.maxTeamSize) {
      errors.maxTeamSize = 'Max team size must be greater than or equal to min team size';
    }

    // Phase Validation
    formData.phases.forEach((phase, index) => {
      if (!phase.name.trim()) {
        errors[`phase_${index}_name`] = 'Phase name is required';
      } else if (phase.name.length > 100) {
        errors[`phase_${index}_name`] = 'Phase name must be 100 characters or less';
      }

      if (!phase.description.trim()) {
        errors[`phase_${index}_description`] = 'Phase description is required';
      } else if (phase.description.length > 500) {
        errors[`phase_${index}_description`] = 'Phase description must be 500 characters or less';
      }

      if (!phase.startDate) {
        errors[`phase_${index}_startDate`] = 'Phase start date is required';
      }

      if (!phase.endDate) {
        errors[`phase_${index}_endDate`] = 'Phase end date is required';
      }

      if (phase.startDate && phase.endDate && new Date(phase.startDate) >= new Date(phase.endDate)) {
        errors[`phase_${index}_endDate`] = 'Phase end date must be after start date';
      }
    });

    // Problem Statement Validation
    formData.problemStatements.forEach((problem, index) => {
      if (!problem.title.trim()) {
        errors[`problem_${index}_title`] = 'Problem title is required';
      } else if (problem.title.length > 200) {
        errors[`problem_${index}_title`] = 'Problem title must be 200 characters or less';
      }

      if (!problem.description.trim()) {
        errors[`problem_${index}_description`] = 'Problem description is required';
      } else if (problem.description.length > 1000) {
        errors[`problem_${index}_description`] = 'Problem description must be 1000 characters or less';
      }

      if (!problem.category.trim()) {
        errors[`problem_${index}_category`] = 'Problem category is required';
      } else if (problem.category.length > 100) {
        errors[`problem_${index}_category`] = 'Problem category must be 100 characters or less';
      }
    });

    // Prize Validation
    formData.prizes.forEach((prize, index) => {
      if (!prize.description.trim()) {
        errors[`prize_${index}_description`] = 'Prize description is required';
      } else if (prize.description.length > 200) {
        errors[`prize_${index}_description`] = 'Prize description must be 200 characters or less';
      }

      if (!prize.value.trim()) {
        errors[`prize_${index}_value`] = 'Prize value is required';
      } else if (prize.value.length > 100) {
        errors[`prize_${index}_value`] = 'Prize value must be 100 characters or less';
      }

      if (prize.rank < 1) {
        errors[`prize_${index}_rank`] = 'Prize rank must be 1 or greater';
      }
    });

    // Rules Validation
    formData.rules.forEach((rule, index) => {
      if (!rule.trim()) {
        errors[`rule_${index}`] = 'Rule cannot be empty';
      } else if (rule.length > 500) {
        errors[`rule_${index}`] = 'Rule must be 500 characters or less';
      }
    });

    return errors;
  };

  const getFieldError = (fieldName: string) => {
    // Always show these errors immediately: date logic errors and basic required fields
    const alwaysShowErrors = ['endDate', 'registrationDeadline', 'title', 'description'];
    const shouldShowImmediately = alwaysShowErrors.includes(fieldName);
    
    console.log(`getFieldError for ${fieldName}:`, {
      shouldShowImmediately,
      hasError: !!validationErrors[fieldName],
      error: validationErrors[fieldName],
      showValidation,
      allErrors: validationErrors
    });
    
    if (shouldShowImmediately && validationErrors[fieldName]) {
      return validationErrors[fieldName];
    }
    return showValidation ? validationErrors[fieldName] : undefined;
  };

  const hasFieldError = (fieldName: string) => {
    // Always show these errors immediately: date logic errors and basic required fields
    const alwaysShowErrors = ['endDate', 'registrationDeadline', 'title', 'description'];
    const shouldShowImmediately = alwaysShowErrors.includes(fieldName);
    if (shouldShowImmediately && validationErrors[fieldName]) {
      return true;
    }
    return showValidation && validationErrors[fieldName];
  };

  // Real-time validation function for date logic
  const validateDateLogic = () => {
    const currentErrors = { ...validationErrors };
    
    // Clear previous date logic errors
    delete currentErrors.endDate;
    delete currentErrors.registrationDeadline;
    
    // End date must be after start date
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      currentErrors.endDate = 'End date and time must be after start date and time';
    }

    // Registration deadline must be before start date
    if (formData.registrationDeadline && formData.startDate && new Date(formData.registrationDeadline) >= new Date(formData.startDate)) {
      currentErrors.registrationDeadline = 'Registration deadline must be before start date and time';
    }

    console.log('Date validation check:', {
      startDate: formData.startDate,
      endDate: formData.endDate, 
      registrationDeadline: formData.registrationDeadline,
      endDateError: currentErrors.endDate,
      regDeadlineError: currentErrors.registrationDeadline
    });

    // Update validation errors with date logic errors
    setValidationErrors(currentErrors);
    
    // Also update step validation to reflect changes
    setTimeout(() => updateStepValidation(), 100);
  };

  const updateStepValidation = () => {
    // Always validate all fields for step indicators (independent of showValidation)
    const errors = validateAllFields();
    
    const basicInfoValid = isBasicInfoValid();
    const problemsValid = isProblemStatementsValid();
    const phasesValid = isPhasesValid();
    const prizesValid = isPrizesValid();  
    const rulesValid = isRulesValid();
    
    const stepValidationState = [
      // Step 0 - Basic Information - check if required fields are filled
      basicInfoValid,
      // Step 1 - Phases & Timeline - valid if no phases or all phases are valid  
      phasesValid,
      // Step 2 - Problem Statements - valid if no problems or all problems are valid
      problemsValid,
      // Step 3 - Prizes & Rewards - valid if no prizes or all prizes are valid
      prizesValid,
      // Step 4 - Rules - valid if no rules or all rules are valid
      rulesValid,
      // Step 5 - Requirements - always valid for now
      true,
      // Step 6 - Review & Submit - valid only if all other steps are valid
      basicInfoValid && phasesValid && problemsValid && prizesValid && rulesValid
    ];
    
    // Debug logging for validation
    console.log('Step Validation Results:', {
      steps: stepValidationState.map((valid, index) => ({
        step: index + 1,
        valid,
        name: ['Basic Information', 'Phases & Timeline', 'Problem Statements', 'Prizes & Rewards', 'Rules', 'Requirements', 'Review & Submit'][index]
      })),
      formData: {
        problemStatementsCount: formData.problemStatements.length,
        phasesCount: formData.phases.length,
        prizesCount: formData.prizes.length,
        rulesCount: formData.rules.length
      }
    });
    
    setStepValidation(stepValidationState);
    return stepValidationState;
  };

  const isProblemStatementsValid = () => {
    // If no problem statements exist, this step is valid
    if (formData.problemStatements.length === 0) {
      return true;
    }
    
    // If problem statements exist, they must all be complete
    return formData.problemStatements.every(problem => 
      problem.title.trim() !== '' && problem.title.length <= 200 &&
      problem.description.trim() !== '' && problem.description.length <= 1000 &&
      problem.category.trim() !== '' && problem.category.length <= 100
    );
  };

  // Initialize step validation on mount and when formData changes
  useEffect(() => {
    updateStepValidation();
  }, [formData]);

  const handleSubmit = async () => {
    console.log('Starting hackathon creation...');
    console.log('Form data:', formData);
    
    // Validate all fields before submission
    const errors = validateAllFields();
    setValidationErrors(errors);
    setShowValidation(true);
    updateStepValidation();

    if (Object.keys(errors).length > 0) {
      // Find which step has errors and navigate to it
      const errorKeys = Object.keys(errors);
      let targetStep = 0;
      
      if (errorKeys.some(key => ['title', 'description', 'startDate', 'endDate', 'registrationDeadline', 'maxTeams', 'minTeamSize', 'maxTeamSize'].includes(key))) {
        targetStep = 0; // Basic Info
      } else if (errorKeys.some(key => key.startsWith('phase_'))) {
        targetStep = 2; // Phases
      } else if (errorKeys.some(key => key.startsWith('problem_'))) {
        targetStep = 1; // Problem Statements
      } else if (errorKeys.some(key => key.startsWith('prize_'))) {
        targetStep = 3; // Prizes
      } else if (errorKeys.some(key => key.startsWith('rule_'))) {
        targetStep = 4; // Rules
      }
      
      setCurrentStep(targetStep);
      error(`Please fix the validation errors before submitting. Found ${Object.keys(errors).length} error(s).`);
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare data for API - ensure all required fields are present and properly formatted
      const hackathonData = {
        ...formData,
        // Ensure dates are properly formatted as ISO strings
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        registrationDeadline: new Date(formData.registrationDeadline).toISOString(),
        // Format phase dates to ISO strings
        phases: formData.phases.map(phase => ({
          ...phase,
          startDate: new Date(phase.startDate).toISOString(),
          endDate: new Date(phase.endDate).toISOString()
        })),
        currentTeams: 0, // Always start with 0 teams
        // createdBy will be set by the backend from the authenticated user
        // mentors and judges will be assigned separately by admin
      };
      
      console.log('Sending hackathon data:', hackathonData);
      
      const result = await hackathonAPI.createHackathon(hackathonData);
      console.log('Hackathon created successfully:', result);
      
      success('Hackathon created successfully!');
      navigate('/admin/hackathons', { state: { refresh: true } });
      
    } catch (err: any) {
      console.error('Error creating hackathon:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Validation errors:', err.response?.data?.errors);
      
      // Handle backend validation errors
      if (err.response?.data?.errors) {
        const backendErrors: {[key: string]: string} = {};
        for (const [field, message] of Object.entries(err.response.data.errors)) {
          backendErrors[field] = Array.isArray(message) ? message[0] : message as string;
        }
        setValidationErrors(backendErrors);
        setShowValidation(true);
      }
      
      error(err.response?.data?.message || 'Failed to create hackathon');
    } finally {
      setSubmitting(false);
    }
  };

  // Step components
  const BasicInfoStep = (
    <div className="space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
          Hackathon Title *
        </label>
        <Input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          maxLength={100}
          placeholder="Enter hackathon title (1-100 characters)"
          className={hasFieldError('title') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
        />
        {getFieldError('title') && (
          <div className="mt-1 text-sm text-red-600">{getFieldError('title')}</div>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
          maxLength={2000}
          rows={4}
          placeholder="Describe the hackathon... (1-2000 characters)"
          className={hasFieldError('description') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
        />
        <div className="mt-1 flex justify-between">
          <div>
            {getFieldError('description') && (
              <div className="text-sm text-red-600">{getFieldError('description')}</div>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {formData.description.length}/2000 characters
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
            Start Date *
          </label>
          <DateTimePicker
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, startDate: value }));
              setTimeout(() => {
                validateDateLogic();
                updateStepValidation();
              }, 100);
            }}
            placeholder="dd/mm/yyyy hh:mm"
            required
            className={hasFieldError('startDate') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            error={getFieldError('startDate')}
          />
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
            End Date *
          </label>
          <DateTimePicker
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, endDate: value }));
              setTimeout(() => {
                validateDateLogic();
                updateStepValidation();
              }, 100);
            }}
            placeholder="dd/mm/yyyy hh:mm"
            required
            className={hasFieldError('endDate') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            error={getFieldError('endDate')}
          />
        </div>

        <div>
          <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700 mb-2">
            Registration Deadline *
          </label>
          <DateTimePicker
            id="registrationDeadline"
            name="registrationDeadline"
            value={formData.registrationDeadline}
            onChange={(value) => {
              console.log('Registration deadline changed to:', value);
              setFormData(prev => ({ ...prev, registrationDeadline: value }));
              setTimeout(() => {
                console.log('Triggering validation for registration deadline');
                validateDateLogic();
                updateStepValidation();
              }, 100);
            }}
            placeholder="dd/mm/yyyy hh:mm"
            required
            className={hasFieldError('registrationDeadline') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            error={getFieldError('registrationDeadline')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label htmlFor="minTeamSize" className="block text-sm font-medium text-gray-700 mb-2">
            Min Team Size
          </label>
          <Input
            type="number"
            id="minTeamSize"
            name="minTeamSize"
            value={formData.minTeamSize}
            onChange={handleInputChange}
            min="1"
            max="5"
            className={hasFieldError('minTeamSize') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {getFieldError('minTeamSize') && (
            <div className="mt-1 text-sm text-red-600">{getFieldError('minTeamSize')}</div>
          )}
        </div>

        <div>
          <label htmlFor="maxTeamSize" className="block text-sm font-medium text-gray-700 mb-2">
            Max Team Size
          </label>
          <Input
            type="number"
            id="maxTeamSize"
            name="maxTeamSize"
            value={formData.maxTeamSize}
            onChange={handleInputChange}
            min="1"
            max="10"
            className={hasFieldError('maxTeamSize') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {getFieldError('maxTeamSize') && (
            <div className="mt-1 text-sm text-red-600">{getFieldError('maxTeamSize')}</div>
          )}
        </div>

        <div>
          <label htmlFor="maxTeams" className="block text-sm font-medium text-gray-700 mb-2">
            Max Teams
          </label>
          <Input
            type="number"
            id="maxTeams"
            name="maxTeams"
            value={formData.maxTeams}
            onChange={handleInputChange}
            min="1"
            max="1000"
            className={hasFieldError('maxTeams') ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {getFieldError('maxTeams') && (
            <div className="mt-1 text-sm text-red-600">{getFieldError('maxTeams')}</div>
          )}
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            Hackathon Status
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="upcoming">Upcoming</option>
            <option value="registration_open">Registration Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="requiresTeamApproval"
          name="requiresTeamApproval"
          checked={formData.requiresTeamApproval}
          onChange={handleInputChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="requiresTeamApproval" className="ml-2 block text-sm text-gray-900">
          Require team approval
        </label>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          name="isActive"
          checked={formData.isActive}
          onChange={handleInputChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
          Active
        </label>
      </div>

      {/* Hackathon Flyer Upload */}
      <div>
        <label htmlFor="hackathonFlyer" className="block text-sm font-medium text-gray-700 mb-2">
          Hackathon Flyer
        </label>
        <div className="space-y-2">
          <input
            type="file"
            id="hackathonFlyer"
            name="hackathonFlyer"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFlyerUpload}
            disabled={uploadingFlyer}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="text-xs text-gray-500">
            Upload a flyer image (JPEG, PNG, GIF, WebP - Max 10MB)
          </p>
          {uploadingFlyer && (
            <div className="flex items-center space-x-2">
              <LoadingSpinner className="w-4 h-4" />
              <span className="text-sm text-gray-600">Uploading...</span>
            </div>
          )}
          {flyerPreview && (
            <div className="mt-2">
              <img
                src={flyerPreview}
                alt="Hackathon flyer preview"
                className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                onError={(e) => console.error('❌ Failed to load flyer preview image:', (e.target as HTMLImageElement)?.src)}
              />
              <button
                type="button"
                onClick={handleRemoveFlyer}
                className="text-red-600 hover:text-red-700 text-sm mt-2 underline"
              >
                Remove Flyer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Show in Home Carousel Checkbox */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="showInHomeCarousel"
          name="showInHomeCarousel"
          checked={formData.showInHomeCarousel}
          onChange={handleInputChange}
          disabled={!formData.hackathonFlyer}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
        />
        <label htmlFor="showInHomeCarousel" className={`ml-2 block text-sm ${!formData.hackathonFlyer ? 'text-gray-400' : 'text-gray-900'}`}>
          Show hackathon flyer in home page carousel
          {!formData.hackathonFlyer && (
            <span className="text-xs text-gray-400 block">
              (Upload a flyer first to enable this option)
            </span>
          )}
        </label>
      </div>
    </div>
  );

  const PhasesStep = (
    <div className="space-y-4">
      {formData.phases.map((phase, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Phase {phase.order}</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removePhase(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <Input
                type="text"
                value={phase.name}
                onChange={(e) => updatePhase(index, 'name', e.target.value)}
                maxLength={100}
                placeholder="e.g., Ideation Phase (max 100 chars)"
                className={hasFieldError(`phase_${index}_name`) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {getFieldError(`phase_${index}_name`) && (
                <div className="mt-1 text-sm text-red-600">{getFieldError(`phase_${index}_name`)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order
              </label>
              <Input
                type="number"
                value={phase.order}
                onChange={(e) => updatePhase(index, 'order', parseInt(e.target.value))}
                min="1"
                placeholder="Phase order number"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <Textarea
              value={phase.description}
              onChange={(e) => updatePhase(index, 'description', e.target.value)}
              maxLength={500}
              placeholder="Describe what happens in this phase... (max 500 chars)"
              rows={3}
              className={hasFieldError(`phase_${index}_description`) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {getFieldError(`phase_${index}_description`) && (
              <div className="mt-1 text-sm text-red-600">{getFieldError(`phase_${index}_description`)}</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <DateTimePicker
                value={phase.startDate}
                onChange={(value) => updatePhase(index, 'startDate', value)}
                placeholder="dd/mm/yyyy hh:mm"
                className={hasFieldError(`phase_${index}_startDate`) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                error={getFieldError(`phase_${index}_startDate`)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <DateTimePicker
                value={phase.endDate}
                onChange={(value) => updatePhase(index, 'endDate', value)}
                placeholder="dd/mm/yyyy hh:mm"
                className={hasFieldError(`phase_${index}_endDate`) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                error={getFieldError(`phase_${index}_endDate`)}
              />
            </div>
          </div>

          {/* Deliverables */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-gray-900">Deliverables</h5>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addDeliverable(index)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Deliverable
              </Button>
            </div>

            {phase.deliverables.map((deliverable, dIndex) => (
              <div key={dIndex} className="border rounded p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Deliverable {dIndex + 1}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeDeliverable(index, dIndex)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <Input
                      type="text"
                      value={deliverable.name}
                      onChange={(e) => updateDeliverable(index, dIndex, 'name', e.target.value)}
                      placeholder="e.g., Project Proposal"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`deliverable-${index}-${dIndex}`}
                      checked={deliverable.required}
                      onChange={(e) => updateDeliverable(index, dIndex, 'required', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`deliverable-${index}-${dIndex}`} className="ml-2 block text-xs text-gray-900">
                      Required
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <Input
                    type="text"
                    value={deliverable.description}
                    onChange={(e) => updateDeliverable(index, dIndex, 'description', e.target.value)}
                    placeholder="Brief description of the deliverable..."
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addPhase}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Phase
      </Button>
    </div>
  );

  const ProblemsStep = (
    <div className="space-y-4">
      {formData.problemStatements.map((problem, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Problem {index + 1}</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removeProblemStatement(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
                              <Input
                  type="text"
                  value={problem.title}
                  onChange={(e) => updateProblemStatement(index, 'title', e.target.value)}
                  maxLength={200}
                  placeholder="Enter problem title (max 200 chars)"
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
                              <Input
                  type="text"
                  value={problem.category}
                  onChange={(e) => updateProblemStatement(index, 'category', e.target.value)}
                  maxLength={100}
                  placeholder="e.g., Web Development, AI/ML (max 100 chars)"
                />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
                          <Textarea
                value={problem.description}
                onChange={(e) => updateProblemStatement(index, 'description', e.target.value)}
                maxLength={1000}
                placeholder="Describe the problem... (max 1000 chars)"
                rows={3}
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulty
            </label>
            <select
              value={problem.difficulty}
              onChange={(e) => updateProblemStatement(index, 'difficulty', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`hiddenFromTeams-${index}`}
              checked={problem.isHiddenFromTeams || false}
              onChange={(e) => updateProblemStatement(index, 'isHiddenFromTeams', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor={`hiddenFromTeams-${index}`} className="text-sm font-medium text-gray-700">
              Hide from team creation (problem statement will not be available for teams to select)
            </label>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addProblemStatement}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Problem Statement
      </Button>
    </div>
  );

  const PrizesStep = (
    <div className="space-y-4">
      {formData.prizes.map((prize, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Prize {prize.rank}</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removePrize(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
                              <Input
                  type="text"
                  value={prize.description}
                  onChange={(e) => updatePrize(index, 'description', e.target.value)}
                  maxLength={200}
                  placeholder="e.g., First Place, Best Innovation (max 200 chars)"
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Value *
              </label>
                              <Input
                  type="text"
                  value={prize.value}
                  onChange={(e) => updatePrize(index, 'value', e.target.value)}
                  maxLength={100}
                  placeholder="e.g., $1000, Laptop, Certificate (max 100 chars)"
                />
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addPrize}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Prize
      </Button>
    </div>
  );

  const RulesStep = (
    <div className="space-y-4">
      {formData.rules.map((rule, index) => (
        <div key={index} className="flex gap-2">
                      <Input
              type="text"
              value={rule}
              onChange={(e) => updateRule(index, e.target.value)}
              maxLength={500}
              placeholder="Enter rule... (max 500 chars)"
            />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => removeRule(index)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addRule}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Rule
      </Button>
    </div>
  );

  const RequirementsStep = (
    <div className="space-y-4">
      {formData.requirements.map((requirement, index) => (
        <div key={index} className="flex gap-2">
                      <Input
              type="text"
              value={requirement}
              onChange={(e) => updateRequirement(index, e.target.value)}
              maxLength={500}
              placeholder="Enter requirement... (max 500 chars)"
            />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => removeRequirement(index)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addRequirement}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Requirement
      </Button>
    </div>
  );

  // Helper function to get field validation status for review
  const getFieldValidationStatus = (fieldName: string) => {
    const errors = validateAllFields();
    return {
      hasError: !!errors[fieldName],
      errorMessage: errors[fieldName]
    };
  };

  const ReviewStep = (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Title *</p>
            <p className={`${getFieldValidationStatus('title').hasError ? 'text-red-600 border border-red-300 rounded px-2 py-1' : 'text-gray-900'}`}>
              {formData.title || <span className="text-red-500 italic">Not provided</span>}
            </p>
            {getFieldValidationStatus('title').hasError && (
              <p className="text-xs text-red-500 mt-1">{getFieldValidationStatus('title').errorMessage}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Status</p>
            <p className="text-gray-900 capitalize">{formData.status.replace('_', ' ')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Start Date *</p>
            <p className={`${getFieldValidationStatus('startDate').hasError ? 'text-red-600 border border-red-300 rounded px-2 py-1' : 'text-gray-900'}`}>
              {formData.startDate ? new Date(formData.startDate).toLocaleString() : <span className="text-red-500 italic">Not provided</span>}
            </p>
            {getFieldValidationStatus('startDate').hasError && (
              <p className="text-xs text-red-500 mt-1">{getFieldValidationStatus('startDate').errorMessage}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">End Date *</p>
            <p className={`${getFieldValidationStatus('endDate').hasError ? 'text-red-600 border border-red-300 rounded px-2 py-1' : 'text-gray-900'}`}>
              {formData.endDate ? new Date(formData.endDate).toLocaleString() : <span className="text-red-500 italic">Not provided</span>}
            </p>
            {getFieldValidationStatus('endDate').hasError && (
              <p className="text-xs text-red-500 mt-1">{getFieldValidationStatus('endDate').errorMessage}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-gray-500">Registration Deadline *</p>
            <p className={`${getFieldValidationStatus('registrationDeadline').hasError ? 'text-red-600 border border-red-300 rounded px-2 py-1' : 'text-gray-900'}`}>
              {formData.registrationDeadline ? new Date(formData.registrationDeadline).toLocaleString() : <span className="text-red-500 italic">Not provided</span>}
            </p>
            {getFieldValidationStatus('registrationDeadline').hasError && (
              <p className="text-xs text-red-500 mt-1">{getFieldValidationStatus('registrationDeadline').errorMessage}</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-500">Description *</p>
          <p className={`${getFieldValidationStatus('description').hasError ? 'text-red-600 border border-red-300 rounded px-2 py-1' : 'text-gray-900'}`}>
            {formData.description || <span className="text-red-500 italic">Not provided</span>}
          </p>
          {getFieldValidationStatus('description').hasError && (
            <p className="text-xs text-red-500 mt-1">{getFieldValidationStatus('description').errorMessage}</p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Min Team Size</p>
            <p className="text-gray-900">{formData.minTeamSize}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Max Team Size</p>
            <p className="text-gray-900">{formData.maxTeamSize}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Max Teams</p>
            <p className="text-gray-900">{formData.maxTeams}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Hackathon Flyer</p>
            {formData.hackathonFlyer ? (
              <div className="mt-2">
                <img
                  src={formData.hackathonFlyer}
                  alt="Hackathon flyer"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                />
              </div>
            ) : (
              <p className="text-gray-400 italic">No flyer uploaded</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Show in Home Carousel</p>
            <p className="text-gray-900">
              {formData.showInHomeCarousel ? (
                <span className="text-green-600">✓ Yes</span>
              ) : (
                <span className="text-gray-500">✗ No</span>
              )}
            </p>
            {formData.showInHomeCarousel && !formData.hackathonFlyer && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Flyer required for carousel display
              </p>
            )}
          </div>
        </div>
      </div>

      {formData.phases.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Phases ({formData.phases.length})</h3>
          <div className="space-y-2">
            {formData.phases.map((phase, index) => {
              const nameStatus = getFieldValidationStatus(`phase_${index}_name`);
              const descStatus = getFieldValidationStatus(`phase_${index}_description`);
              const startDateStatus = getFieldValidationStatus(`phase_${index}_startDate`);
              const endDateStatus = getFieldValidationStatus(`phase_${index}_endDate`);
              const hasAnyError = nameStatus.hasError || descStatus.hasError || startDateStatus.hasError || endDateStatus.hasError;
              
              return (
                <div key={index} className={`border rounded p-3 ${hasAnyError ? 'border-red-300 bg-red-50' : ''}`}>
                  <p className={`font-medium ${nameStatus.hasError ? 'text-red-600' : ''}`}>
                    {phase.name || <span className="text-red-500 italic">Name not provided</span>}
                  </p>
                  {nameStatus.hasError && <p className="text-xs text-red-500">{nameStatus.errorMessage}</p>}
                  
                  <p className={`text-sm ${descStatus.hasError ? 'text-red-600' : 'text-gray-600'}`}>
                    {phase.description || <span className="text-red-500 italic">Description not provided</span>}
                  </p>
                  {descStatus.hasError && <p className="text-xs text-red-500">{descStatus.errorMessage}</p>}
                  
                  <p className={`text-xs ${(startDateStatus.hasError || endDateStatus.hasError) ? 'text-red-500' : 'text-gray-500'}`}>
                    {phase.startDate ? new Date(phase.startDate).toLocaleDateString() : 'Start date not set'} - {phase.endDate ? new Date(phase.endDate).toLocaleDateString() : 'End date not set'}
                  </p>
                  {(startDateStatus.hasError || endDateStatus.hasError) && (
                    <p className="text-xs text-red-500">
                      {startDateStatus.errorMessage} {endDateStatus.errorMessage}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {formData.problemStatements.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Problem Statements ({formData.problemStatements.length})</h3>
          <div className="space-y-2">
            {formData.problemStatements.map((problem, index) => {
              const titleStatus = getFieldValidationStatus(`problem_${index}_title`);
              const descStatus = getFieldValidationStatus(`problem_${index}_description`);
              const categoryStatus = getFieldValidationStatus(`problem_${index}_category`);
              const hasAnyError = titleStatus.hasError || descStatus.hasError || categoryStatus.hasError;
              
              return (
                <div key={index} className={`border rounded p-3 ${hasAnyError ? 'border-red-300 bg-red-50' : ''}`}>
                  <p className={`font-medium ${titleStatus.hasError ? 'text-red-600' : ''}`}>
                    {problem.title || <span className="text-red-500 italic">Title not provided</span>}
                  </p>
                  {titleStatus.hasError && <p className="text-xs text-red-500">{titleStatus.errorMessage}</p>}
                  
                  <p className={`text-sm ${categoryStatus.hasError ? 'text-red-600' : 'text-gray-600'}`}>
                    {problem.category || <span className="text-red-500 italic">Category not provided</span>} • {problem.difficulty}
                  </p>
                  {categoryStatus.hasError && <p className="text-xs text-red-500">{categoryStatus.errorMessage}</p>}
                  
                  {problem.description && (
                    <p className={`text-sm mt-1 ${descStatus.hasError ? 'text-red-600' : 'text-gray-700'}`}>
                      {problem.description}
                    </p>
                  )}
                  {descStatus.hasError && <p className="text-xs text-red-500">{descStatus.errorMessage}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {formData.prizes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Prizes ({formData.prizes.length})</h3>
          <div className="space-y-2">
            {formData.prizes.map((prize, index) => {
              const descStatus = getFieldValidationStatus(`prize_${index}_description`);
              const valueStatus = getFieldValidationStatus(`prize_${index}_value`);
              const rankStatus = getFieldValidationStatus(`prize_${index}_rank`);
              const hasAnyError = descStatus.hasError || valueStatus.hasError || rankStatus.hasError;
              
              return (
                <div key={index} className={`border rounded p-3 ${hasAnyError ? 'border-red-300 bg-red-50' : ''}`}>
                  <p className={`font-medium ${descStatus.hasError ? 'text-red-600' : ''}`}>
                    {prize.description || <span className="text-red-500 italic">Description not provided</span>}
                  </p>
                  {descStatus.hasError && <p className="text-xs text-red-500">{descStatus.errorMessage}</p>}
                  
                  <p className={`text-sm ${valueStatus.hasError ? 'text-red-600' : 'text-gray-600'}`}>
                    Rank {prize.rank}: {prize.value || <span className="text-red-500 italic">Value not provided</span>}
                  </p>
                  {valueStatus.hasError && <p className="text-xs text-red-500">{valueStatus.errorMessage}</p>}
                  {rankStatus.hasError && <p className="text-xs text-red-500">{rankStatus.errorMessage}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(formData.rules.length > 0 || formData.requirements.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {formData.rules.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Rules ({formData.rules.length})</h3>
              <ul className="list-disc list-inside space-y-1">
                {formData.rules.map((rule, index) => {
                  const ruleStatus = getFieldValidationStatus(`rule_${index}`);
                  return (
                    <li key={index} className={`text-sm ${ruleStatus.hasError ? 'text-red-600' : 'text-gray-700'}`}>
                      {rule || <span className="text-red-500 italic">Rule not provided</span>}
                      {ruleStatus.hasError && (
                        <div className="text-xs text-red-500 ml-4">{ruleStatus.errorMessage}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {formData.requirements.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Requirements ({formData.requirements.length})</h3>
              <ul className="list-disc list-inside space-y-1">
                {formData.requirements.map((requirement, index) => (
                  <li key={index} className="text-sm text-gray-700">{requirement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const steps = [
    {
      title: 'Basic Information',
      content: BasicInfoStep
    },
    {
      title: 'Phases & Timeline',
      content: PhasesStep
    },
    {
      title: 'Problem Statements',
      content: ProblemsStep
    },
    {
      title: 'Prizes & Rewards',
      content: PrizesStep
    },
    {
      title: 'Rules',
      content: RulesStep
    },
    {
      title: 'Requirements',
      content: RequirementsStep
    },
    {
      title: 'Review & Submit',
      content: ReviewStep
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Hackathon</h1>
        <p className="text-gray-600">Set up a new hackathon with multiple phases and detailed configuration</p>
      </div>

      <FormWizard
        steps={steps}
        onComplete={handleSubmit}
        loading={submitting}
        submitText="Create Hackathon"
        stepValidation={stepValidation}
        currentStep={currentStep}
        onStepChange={(step) => {
          setCurrentStep(step);
          if (showValidation) {
            updateStepValidation();
          }
        }}
      />
    </div>
  );
};

export default HackathonCreate; 